<template>
  <div class="border border-2 rounded-lg mt-3 pb-2">
    <h6 class="mb-0 p-1">
      {{ $t("Activities") }}
    </h6>

    <app-timeline class="activities px-2 m-0">
      <app-timeline-item
        v-for="activity in visibleActivities"
        :key="activity.uuid"
        :icon="getTaskIcon(activity)"
      >
        <component
          :is="getActivityComponent(activity.__typename, activity.actionType)"
          :activity="activity"
        />
      </app-timeline-item>

      <div
        v-if="hasMoreActivities"
        ref="loadMoreTrigger"
        style="height: 1px;"
      />
    </app-timeline>

    <div
      v-if="!activities || !activities.length"
      class="text-muted px-1"
    >
      {{ $t("No activity yet") }}
    </div>
  </div>
</template>

<script>
import { BAvatar } from 'bootstrap-vue'
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import TaskCreatedActivity from './components/TaskCreatedActivity.vue'
import TaskUpdatedActivity from './components/TaskUpdatedActivity.vue'
import TaskAssignedActivity from './components/TaskAssignedActivity.vue'
import TaskStatusChangedActivity from './components/TaskStatusChangedActivity.vue'
import TaskLabelAttachedActivity from './components/TaskLabelAttachedActivity.vue'
import TaskLabelDetachedActivity from './components/TaskLabelDetachedActivity.vue'
import TaskMilestoneAttachedActivity from './components/TaskMilestoneAttachedActivity.vue'
import TaskMilestoneDetachedActivity from './components/TaskMilestoneDetachedActivity.vue'
import TaskDueDateChangedActivity from './components/TaskDueDateChangedActivity.vue'
import TaskDeletedActivity from './components/TaskDeletedActivity.vue'
import CommentActivity from './components/CommentActivity.vue'
import DefaultActivityComponent from './components/ActivityComponent.vue'
import parseMarkdown from '../markdown'
import TaskMentionActivity from './components/TaskMentionActivity.vue'
import TaskMentionChangedActivity from './components/TaskMentionChangedActivity.vue'

export default {
  components: {
    AppTimeline,
    AppTimelineItem,
    BAvatar,
    TaskCreatedActivity,
    TaskAssignedActivity,
    TaskUpdatedActivity,
    TaskStatusChangedActivity,
    TaskLabelAttachedActivity,
    TaskLabelDetachedActivity,
    TaskMilestoneAttachedActivity,
    TaskMilestoneDetachedActivity,
    TaskDueDateChangedActivity,
    TaskDeletedActivity,
    CommentActivity,
    DefaultActivityComponent,
  },
  props: {
    activities: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      parseMarkdown,
      pageSize: 10,
      visibleCount: 10,
      observer: null,
    }
  },
  computed: {
    visibleActivities() {
      return this.activities.slice(0, this.visibleCount)
    },
    hasMoreActivities() {
      return this.visibleCount < this.activities.length
    },
  },
  watch: {
    activities() {
      this.visibleCount = this.pageSize
      this.$nextTick(this.initInfiniteScroll)
    },
  },
  mounted() {
    this.initInfiniteScroll()
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    getActivityComponent(typename, actionType) {
      const componentMap = {
        'task.comment_added': CommentActivity,
        'task.comment_deleted': CommentActivity,
        'task.comment_edited': CommentActivity,
        'task.mentions_added': TaskMentionActivity,
        'task.mentions_changed': TaskMentionChangedActivity,
        TaskCreatedActivity,
        TaskAssignedActivity,
        TaskUpdatedActivity,
        TaskStatusChangedActivity,
        TaskLabelAttachedActivity,
        TaskLabelDetachedActivity,
        TaskMilestoneAttachedActivity,
        TaskMilestoneDetachedActivity,
        TaskDueDateChangedActivity,
        TaskDeletedActivity,
      }
      return componentMap[actionType] || componentMap[typename] || 'DefaultActivityComponent'
    },
    getScrollParent(node) {
      if (!node || node === document.documentElement) return null
      const { overflowY } = window.getComputedStyle(node)
      if (overflowY === 'auto' || overflowY === 'scroll') return node
      return this.getScrollParent(node.parentElement)
    },
    initInfiniteScroll() {
      if (this.observer) this.observer.disconnect()
      if (!this.$refs.loadMoreTrigger) return
      const scrollParent = this.getScrollParent(this.$el)
      this.observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) this.loadMore()
        },
        { root: scrollParent, rootMargin: '200px', threshold: 0 },
      )
      this.observer.observe(this.$refs.loadMoreTrigger)
    },
    loadMore() {
      if (!this.hasMoreActivities) return
      this.visibleCount = Math.min(this.visibleCount + this.pageSize, this.activities.length)
      this.$nextTick(this.initInfiniteScroll)
    },
    getTaskIcon(activity) {
      switch (activity.actionType) {
      case 'task.mentions_added':
      case 'task.mentions_changed':
        return 'AtSignIcon'
      case 'task.comment_added':
      case 'task.comment_deleted':
      case 'task.comment_edited':
        return 'MessageCircleIcon'
      case 'task.created':
        return 'PlusIcon'
      case 'task.updated':
        return 'FeatherIcon'
      case 'task.status_changed':
        return 'ArrowRightIcon'
      case 'task.assigned':
        if (activity.newAssignee) return 'UserPlusIcon'
        return 'UserXIcon'
      case 'task.label_attached':
        return 'TagIcon'
      case 'task.label_detached':
        return 'XCircleIcon'
      case 'task.milestone_attached':
        return 'CrosshairIcon'
      case 'task.milestone_detached':
        return 'XCircleIcon'
      case 'task.due_date_changed':
        return 'CalendarIcon'
      case 'task.asset_uploaded':
        return 'FilePlusIcon'
      case 'task.deleted':
        return 'TrashIcon'
      default:
        return 'PlusIcon'
      }
    },
  },
}

</script>

<style lang="scss" scoped>
.activities {
  max-height: 460px !important;
  overflow-y: auto;
}
</style>
